import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { getExerciseMediaIndex } from '../utils/exerciseMedia';
import { normalizeExerciseName } from '../utils/exerciseCatalog';
import { ExerciseDemoModal } from '../components/ExerciseDemoModal';

interface ExerciseDemoContextValue {
  /** True only once the media index is loaded AND the exercise has demo images. */
  hasDemo: (name: string) => boolean;
  /** Opens the shared demo modal for the given exercise. */
  openDemo: (name: string) => void;
}

const ExerciseDemoContext = createContext<ExerciseDemoContextValue>({
  hasDemo: () => false,
  openDemo: () => {},
});

/**
 * Loads the exercise media index once and exposes a synchronous `hasDemo` check
 * plus a single shared demo modal that any component can open. Mount near the
 * app root so the demo is reachable from every screen that lists exercises.
 */
export function ExerciseDemoProvider({ children }: { children: React.ReactNode }) {
  const [available, setAvailable] = useState<Set<string> | null>(null);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAvailableExercises = async () => {
      try {
        const index = await getExerciseMediaIndex();
        if (cancelled) return;
        const exercisesWithImages = new Set<string>();
        index.forEach((media, key) => {
          if (media.images.length > 0) exercisesWithImages.add(key);
        });
        setAvailable(exercisesWithImages);
      } catch {
        if (!cancelled) setAvailable(new Set());
      }
    };

    void loadAvailableExercises();
    window.addEventListener('online', loadAvailableExercises);

    return () => {
      cancelled = true;
      window.removeEventListener('online', loadAvailableExercises);
    };
  }, []);

  const hasDemo = useCallback(
    (name: string) => !!available && available.has(normalizeExerciseName(name)),
    [available]
  );

  const openDemo = useCallback((name: string) => setActive(name), []);
  const closeDemo = useCallback(() => setActive(null), []);
  const contextValue = useMemo(
    () => ({ hasDemo, openDemo }),
    [hasDemo, openDemo]
  );

  return (
    <ExerciseDemoContext.Provider value={contextValue}>
      {children}
      <ExerciseDemoModal exerciseName={active} onClose={closeDemo} />
    </ExerciseDemoContext.Provider>
  );
}

export const useExerciseDemo = () => useContext(ExerciseDemoContext);
